import { useCallback, useEffect, useRef, useState } from 'react';

import { useServiceFeatures } from 'hooks/useServiceContext';
import { getCommodities } from 'lib/api/getCommodities';
import { TMSCommodity } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';

import { useQuickQuoteContext } from '../../context/QuickQuoteContext';

/**
 * Fetches Tai commodities for the selected LTL customer.
 */
export function useTaiCommodities(): {
  commodities: Maybe<TMSCommodity[]>;
  isLoading: boolean;
  isCatalogReady: boolean;
  hasCustomer: boolean;
  hasTmsId: boolean;
  hasCustomerId: boolean;
} {
  const { ltlCustomer } = useQuickQuoteContext();
  const { ltlTMSIntegrations } = useServiceFeatures();
  const [commodities, setCommodities] = useState<Maybe<TMSCommodity[]>>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadedCustomerKey, setLoadedCustomerKey] =
    useState<Maybe<string>>(null);
  const requestIdRef = useRef(0);

  const taiTmsId =
    ltlTMSIntegrations?.find((integration) => integration.name === TMS.Tai)
      ?.id ?? ltlTMSIntegrations?.[0]?.id;

  const customerDbId = ltlCustomer?.customerDbId;
  const customerExternalId = ltlCustomer?.externalId ?? ltlCustomer?.customerId;
  const customerKey = `${customerDbId ?? ''}:${customerExternalId ?? ''}`;

  const fetchCommodities = useCallback(async () => {
    if (!taiTmsId || (!customerDbId && !customerExternalId)) {
      setCommodities(null);
      setLoadedCustomerKey(null);
      return;
    }

    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    const res = await getCommodities(
      taiTmsId,
      false,
      false,
      customerDbId,
      undefined,
      customerExternalId
    );
    if (requestId !== requestIdRef.current) {
      return;
    }
    setIsLoading(false);

    if (res.isOk()) {
      setCommodities(res.value.commodityList ?? []);
    } else {
      setCommodities([]);
    }
    setLoadedCustomerKey(customerKey);
  }, [taiTmsId, customerDbId, customerExternalId, customerKey]);

  useEffect(() => {
    if (!taiTmsId || (!customerDbId && !customerExternalId)) {
      setCommodities(null);
      setLoadedCustomerKey(null);
      return;
    }

    fetchCommodities();
  }, [taiTmsId, customerDbId, customerExternalId, fetchCommodities]);

  return {
    commodities,
    isLoading,
    isCatalogReady:
      !isLoading && loadedCustomerKey === customerKey && commodities !== null,
    hasCustomer: Boolean(ltlCustomer),
    hasTmsId: Boolean(taiTmsId),
    hasCustomerId: Boolean(customerDbId),
  };
}
